import React, { useEffect } from 'react'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import Modal from 'react-responsive-modal'
import 'react-responsive-modal/styles.css'
import { IoIosCheckmark, IoIosClose } from 'react-icons/io'
import styles from './styles.module.scss'
import { useDispatch, useSelector } from 'react-redux'
import { getAreas } from '../../actions/areas'
import { getRegions } from '../../actions/regions'
import cn from 'classnames'

const ServerModal = ({ isOpen, onClose, editingServer, onSubmit, loading }) => {
  const dispatch = useDispatch()
  const { areas } = useSelector(state => state.areas)
  const { regions } = useSelector(state => state.regions)

  const getInitialValues = () => ({
    id: editingServer?.id || '',
    name: editingServer?.name || '',
    archive: editingServer?.archive || false,
    region: editingServer?.region || '',
    area: editingServer?.area || '',
    hostlocal: editingServer?.hostlocal || '',
    port: editingServer?.port || '',
    hostglobal: editingServer?.hostglobal || '',
    db: editingServer?.db || '',
    newservice: editingServer?.newservice || false,
    webquery: editingServer?.webquery === 1 || editingServer?.webquery === true,
    notify: editingServer?.notify === 1 || editingServer?.notify === true,
    free: editingServer?.free || false,
    download: editingServer?.download === 1 || editingServer?.download === true,
    dbtype: editingServer?.dbtype || 0,
    expirationdate: editingServer?.expirationdate 
      ? editingServer.expirationdate.split('T')[0] 
      : '',
    updatetodate: editingServer?.updatetodate 
      ? editingServer.updatetodate.split('T')[0] 
      : '',
    site: editingServer?.site || '',
    cache: editingServer?.cache === 1 || editingServer?.cache === true,
    use_proxy: editingServer?.use_proxy === 1 || editingServer?.use_proxy === true,
    comment: editingServer?.comment || '',
    price_plan: editingServer?.price_plan || 0,
    connstd: editingServer?.connstd || 0,
    connkiosk: editingServer?.connkiosk || 0,
    connmenu: editingServer?.connmenu || 0,
    conncons: editingServer?.conncons || 0,
    proxy_servername: editingServer?.proxy_servername || '',
    proxy_host: editingServer?.proxy_host || '',
    keynum: editingServer?.keynum || '',
    keyid: editingServer?.keyid || '',
    contacts: editingServer?.contacts || '',
    proxy_port: editingServer?.proxy_port || ''
  })

  const validationSchema = Yup.object({
    id: Yup.string().required('Поле id обязательно для заполнения'),
    port: Yup.number()
      .typeError('Порт должен быть числом')
      .required('Поле порт обязательно для заполнения')
      .positive('Порт должен быть положительным числом')
      .integer('Порт должен быть целым числом'),
    area: Yup.string().required('Поле населенный пункт обязательно для заполнения'),
    expirationdate: Yup.date()
      .typeError('Неверный формат даты')
      .required('Поле срок действия лицензии обязательно для заполнения'),
    updatetodate: Yup.date()
      .typeError('Неверный формат даты')
      .required('Поле поддерж. до обязательно для заполнения')
  })

  const formik = useFormik({
    initialValues: getInitialValues(),
    enableReinitialize: true,
    validationSchema,
    onSubmit: async ({id, region, ...values}, { setSubmitting }) => {
      const serverData = {
        ...values,
        region: region ? parseInt(region) : 0,
        port: values.port ? parseInt(values.port) : null,
        webquery: values.webquery ? 1 : 0,
        notify: values.notify ? 1 : 0,
        download: values.download ? 1 : 0,
        cache: values.cache ? 1 : 0,
        use_proxy: values.use_proxy ? 1 : 0,
        connstd: values.connstd ? parseInt(values.connstd) : 0,
        connkiosk: values.connkiosk ? parseInt(values.connkiosk) : 0,
        connmenu: values.connmenu ? parseInt(values.connmenu) : 0,
        conncons: values.conncons ? parseInt(values.conncons) : 0,
        proxy_port: values.proxy_port ? parseInt(values.proxy_port) : null,
        dbtype: values.dbtype ? parseInt(values.dbtype) : 0,
        price_plan: values.price_plan ? parseInt(values.price_plan) : null,
        expirationdate: values.expirationdate || null,
        updatetodate: values.updatetodate || null,
        site: values.site || '',
        comment: values.comment || '',
        contacts: values.contacts || ''
      }
      
      await onSubmit(serverData, editingServer?.id)
      setSubmitting(false)
    }
  })

  useEffect(() => {
    dispatch(getRegions())
  }, [dispatch])

  useEffect(() => {
    if (formik.values.region) {
      dispatch(getAreas(formik.values.region))
      // Сбрасываем выбранный населенный пункт при смене региона
      if (formik.values.area) {
        formik.setFieldValue('area', '')
      }
    } else {
      dispatch(getAreas())
    }
  }, [formik.values.region, dispatch])

  const getOptions = (items) => 
    items?.map(({ id, name }) => (
      <option key={id} value={id}>
        {name}
      </option>
    ))

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      center
      classNames={{
        modal: styles.modal
      }}
    >
      <h2 className={styles.modalTitle}>
        {editingServer ? 'Редактировать сервер' : 'Новая запись'}
      </h2>
      <form onSubmit={formik.handleSubmit} className={styles.form}>
        {/* Секция 1: Общая информация */}
        <div className={styles.section}>
          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label htmlFor="id">id</label>
              <input
                type="text"
                id="id"
                name="id"
                className={cn(styles.input, { [styles.inputError]: formik.touched.id && formik.errors.id })}
                disabled={!!editingServer}
                value={formik.values.id}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
              {formik.touched.id && formik.errors.id && (
                <span className={styles.errorMessage}>{formik.errors.id}</span>
              )}
            </div>
            <div className={styles.formGroup}>
              <label htmlFor="name">Наименование</label>
              <input
                type="text"
                id="name"
                name="name"
                className={styles.input}
                value={formik.values.name}
                onChange={formik.handleChange}
              />
            </div>
            <div className={styles.formGroup}>
              <label className={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  name="archive"
                  className={styles.checkbox}
                  checked={formik.values.archive}
                  onChange={formik.handleChange}
                />
                <span>Архив</span>
              </label>
            </div>
            <div className={styles.formGroup}>
              <label htmlFor="region">Регион</label>
              <select
                id="region"
                name="region"
                className={styles.select}
                value={formik.values.region}
                onChange={(e) => {
                  formik.handleChange(e)
                  if (e.target.value) {
                    dispatch(getAreas(e.target.value))
                    formik.setFieldValue('area', '')
                  } else {
                    dispatch(getAreas())
                  }
                }}
              >
                <option value="">Выберите регион</option>
                {getOptions(regions)}
              </select>
            </div>
            <div className={styles.formGroup}>
              <label htmlFor="area">Населенный пункт</label>
              <select
                id="area"
                name="area"
                className={cn(styles.select, { [styles.inputError]: formik.touched.area && formik.errors.area })}
                value={formik.values.area}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              >
                <option value="">Выберите населенный пункт</option>
                {getOptions(areas)}
              </select>
              {formik.touched.area && formik.errors.area && (
                <span className={styles.errorMessage}>{formik.errors.area}</span>
              )}
            </div>
          </div>
        </div>

        {/* Секция 2: IP и порт */}
        <div className={styles.section}>
          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label htmlFor="hostlocal">Локал. IP</label>
              <input
                type="text"
                id="hostlocal"
                name="hostlocal"
                className={styles.input}
                value={formik.values.hostlocal}
                onChange={formik.handleChange}
              />
            </div>
            <div className={styles.formGroup}>
              <label htmlFor="port">Порт</label>
              <input
                type="number"
                id="port"
                name="port"
                className={styles.input}
                value={formik.values.port}
                onChange={formik.handleChange}
              />
            </div>
            <div className={styles.formGroup}>
              <label htmlFor="hostglobal">Глобал. IP</label>
              <input
                type="text"
                id="hostglobal"
                name="hostglobal"
                className={styles.input}
                value={formik.values.hostglobal}
                onChange={formik.handleChange}
              />
            </div>
            <div className={styles.formGroup}>
              <label htmlFor="db">База данных</label>
              <input
                type="text"
                id="db"
                name="db"
                className={styles.input}
                value={formik.values.db}
                onChange={formik.handleChange}
              />
            </div>
          </div>
        </div>

        {/* Секция 3: Службы и детали БД */}
        <div className={styles.section}>
          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label className={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  name="newservice"
                  className={styles.checkbox}
                  checked={formik.values.newservice}
                  onChange={formik.handleChange}
                />
                <span>Служба Nidb</span>
              </label>
            </div>
            <div className={styles.formGroup}>
              <label className={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  name="webquery"
                  className={styles.checkbox}
                  checked={formik.values.webquery}
                  onChange={formik.handleChange}
                />
                <span>Web Query</span>
              </label>
            </div>
            <div className={styles.formGroup}>
              <label className={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  name="notify"
                  className={styles.checkbox}
                  checked={formik.values.notify}
                  onChange={formik.handleChange}
                />
                <span>SMS/ email</span>
              </label>
            </div>
            <div className={styles.formGroup}>
              <label className={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  name="free"
                  className={styles.checkbox}
                  checked={formik.values.free}
                  onChange={formik.handleChange}
                />
                <span>Беспл.</span>
              </label>
            </div>
            <div className={styles.formGroup}>
              <label className={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  name="download"
                  className={styles.checkbox}
                  checked={formik.values.download}
                  onChange={formik.handleChange}
                />
                <span>Загр.</span>
              </label>
            </div>
            <div className={styles.formGroup}>
              <label htmlFor="dbtype">Тип БД</label>
              <select
                id="dbtype"
                name="dbtype"
                className={styles.select}
                value={formik.values.dbtype}
                onChange={formik.handleChange}
              >
                <option value="0">Выберите тип БД</option>
                {/* Здесь можно добавить опции из API */}
              </select>
            </div>
            <div className={styles.formGroup}>
              <label htmlFor="updatetodate">Поддерж. до</label>
              <input
                type="date"
                id="updatetodate"
                name="updatetodate"
                className={cn(styles.input, { [styles.inputError]: formik.touched.updatetodate && formik.errors.updatetodate })}
                value={formik.values.updatetodate}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
              {formik.touched.updatetodate && formik.errors.updatetodate && (
                <span className={styles.errorMessage}>{formik.errors.updatetodate}</span>
              )}
            </div>
            <div className={styles.formGroup}>
              <label htmlFor="site">Сайт</label>
              <input
                type="text"
                id="site"
                name="site"
                className={styles.input}
                value={formik.values.site}
                onChange={formik.handleChange}
              />
            </div>
            <div className={styles.formGroup}>
              <label className={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  name="cache"
                  className={styles.checkbox}
                  checked={formik.values.cache}
                  onChange={formik.handleChange}
                />
                <span>Кэшировать</span>
              </label>
            </div>
            <div className={styles.formGroup}>
              <label className={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  name="use_proxy"
                  className={styles.checkbox}
                  checked={formik.values.use_proxy}
                  onChange={formik.handleChange}
                />
                <span>Исп. прокси для "Интернет версии"</span>
              </label>
            </div>
          </div>
        </div>

        {/* Секция 4: Примечания, лицензии, прокси */}
        <div className={styles.section}>
          <div className={styles.formRow}>
            <div className={styles.formGroupFull}>
              <label htmlFor="comment">Примечание(TeamViewer, и т.д.)</label>
              <textarea
                id="comment"
                name="comment"
                className={styles.textarea}
                rows="3"
                value={formik.values.comment}
                onChange={formik.handleChange}
              />
            </div>
            <div className={styles.formGroup}>
              <label htmlFor="price_plan">Тарифный план</label>
              <select
                id="price_plan"
                name="price_plan"
                className={styles.select}
                value={formik.values.price_plan}
                onChange={formik.handleChange}
              >
                <option value="0">Выберите тарифный план</option>
                {/* Здесь можно добавить опции из API */}
              </select>
            </div>
            <div className={styles.formGroup}>
              <label htmlFor="connstd">Обычных лицензий</label>
              <input
                type="number"
                id="connstd"
                name="connstd"
                className={styles.input}
                value={formik.values.connstd}
                onChange={formik.handleChange}
              />
            </div>
            <div className={styles.formGroup}>
              <label htmlFor="connkiosk">Лицензий 'Киоск'</label>
              <input
                type="number"
                id="connkiosk"
                name="connkiosk"
                className={styles.input}
                value={formik.values.connkiosk}
                onChange={formik.handleChange}
              />
            </div>
            <div className={styles.formGroup}>
              <label htmlFor="connmenu">Лицензий 'Столовая'</label>
              <input
                type="number"
                id="connmenu"
                name="connmenu"
                className={styles.input}
                value={formik.values.connmenu}
                onChange={formik.handleChange}
              />
            </div>
            <div className={styles.formGroup}>
              <label htmlFor="conncons">Лицензий 'Консолидированная'</label>
              <input
                type="number"
                id="conncons"
                name="conncons"
                className={styles.input}
                value={formik.values.conncons}
                onChange={formik.handleChange}
              />
            </div>
            <div className={styles.formGroup}>
              <label htmlFor="expirationdate">Срок действия лицензии</label>
              <input
                type="date"
                id="expirationdate"
                name="expirationdate"
                className={cn(styles.input, { [styles.inputError]: formik.touched.expirationdate && formik.errors.expirationdate })}
                value={formik.values.expirationdate}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
              {formik.touched.expirationdate && formik.errors.expirationdate && (
                <span className={styles.errorMessage}>{formik.errors.expirationdate}</span>
              )}
            </div>
            <div className={styles.formGroup}>
              <label htmlFor="proxy_servername">Proxy ID</label>
              <input
                type="text"
                id="proxy_servername"
                name="proxy_servername"
                className={styles.input}
                value={formik.values.proxy_servername}
                onChange={formik.handleChange}
              />
            </div>
            <div className={styles.formGroup}>
              <label htmlFor="proxy_host">Proxy IP</label>
              <input
                type="text"
                id="proxy_host"
                name="proxy_host"
                className={styles.input}
                value={formik.values.proxy_host}
                onChange={formik.handleChange}
              />
            </div>
            <div className={styles.formGroup}>
              <label htmlFor="keynum">Номер ключа</label>
              <input
                type="text"
                id="keynum"
                name="keynum"
                className={styles.input}
                value={formik.values.keynum}
                onChange={formik.handleChange}
              />
            </div>
            <div className={styles.formGroup}>
              <label htmlFor="keyid">ID ключа</label>
              <input
                type="text"
                id="keyid"
                name="keyid"
                className={styles.input}
                value={formik.values.keyid}
                onChange={formik.handleChange}
              />
            </div>
            <div className={styles.formGroupFull}>
              <label htmlFor="contacts">Контактное лицо</label>
              <textarea
                id="contacts"
                name="contacts"
                className={styles.textarea}
                rows="3"
                value={formik.values.contacts}
                onChange={formik.handleChange}
              />
            </div>
            <div className={styles.formGroup}>
              <label htmlFor="proxy_port">Proxy порт</label>
              <input
                type="number"
                id="proxy_port"
                name="proxy_port"
                className={styles.input}
                value={formik.values.proxy_port}
                onChange={formik.handleChange}
              />
            </div>
          </div>
        </div>

        {/* Кнопки действий */}
        <div className={styles.formActions}>
          <button
            type="button"
            onClick={onClose}
            className={styles.cancelButton}
            disabled={formik.isSubmitting || loading}
          >
            <IoIosClose size={24} />
          </button>
          <button
            type="submit"
            className={styles.submitButton}
            disabled={formik.isSubmitting || loading}
          >
            <IoIosCheckmark size={24} />
          </button>
        </div>
      </form>
    </Modal>
  )
}

export default ServerModal
